<script setup>
/**
 * RED-310 — collapsed form: one line echoing the conditions of the shown
 * result (`result.title` + `result.conditions`) and a "Muuda tingimusi" link
 * that re-expands the form.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { mapFilterLabel, mapFilterValue } from '@/services/mapFilterText'
import { useAnalyticsStore } from '@/stores/analyticsStore'

const { t, te } = useI18n()
const store = useAnalyticsStore()

/**
 * "Tubade arv 4 – 4" for each map filter the server applied, read from the
 * sent filter (raw bounds), falling back to the server's own text.
 */
const appliedFilterText = applied => {
  const sent = store.lastPayload?.map_filters ?? []
  return applied.map(filter => {
    const raw = sent.find(item => item.key === filter.key)
    const label = mapFilterLabel(filter.key, { t, te })
    const value = raw ? mapFilterValue(raw, { t }) : filter.value
    return raw?.type === 'switch' || !value ? label : `${label} ${value}`
  })
}

const parts = computed(() => {
  const r = store.result
  const conditions = r?.conditions ?? {}
  const out = []
  if (r?.title?.report) out.push(r.title.report)
  if (r?.title?.location) out.push(r.title.location)
  if (r?.title?.period) out.push(r.title.period)
  const trim = Number(conditions.trim_percent ?? store.form.trim ?? 0)
  out.push(`${t('messages.transactions.analytics.form.trim')} ${trim} %`)
  const applied = conditions.applied_map_filters ?? []
  if (conditions.use_map_filters && applied.length) {
    out.push(
      t('messages.transactions.analytics.form.map_filters_applied', {
        list: appliedFilterText(applied).join(', '),
      }),
    )
  }
  return out
})

const emit = defineEmits(['edit'])
</script>

<template>
  <div
    class="flex items-start justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
    data-testid="txn-analytics-conditions-summary"
  >
    <p class="text-sm text-gray-700 leading-6">
      <template v-for="(part, index) in parts" :key="index">
        <span v-if="index > 0" class="text-gray-400"> · </span>
        <span>{{ part }}</span>
      </template>
    </p>
    <Button
      :label="$t('messages.transactions.analytics.form.edit')"
      icon="pi pi-pencil"
      variant="link"
      size="small"
      class="shrink-0 w-auto! px-0!"
      data-testid="txn-analytics-edit"
      @click="emit('edit')"
    />
  </div>
</template>
